import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronRight, FileDown } from "lucide-react";

// Phase 25.13ao · P0 — Sticky bottom-bar CTA for the public site.
//
// Why it exists: 30-day funnel analysis showed 60 unique visitors → 1
// lead (0.7%). The on-site mechanism for capturing intent is too weak.
// This bar surfaces a low-friction CTA ("Download founder's guide — no
// email required") that sticks to the bottom of every public page so
// visitors who would otherwise bounce have an obvious next step.
//
// UX rules we obey:
//   • Hidden on first 800px of scroll so the hero gets its moment
//   • Dismissable with X — dismissal persists for 7 days via localStorage
//   • Hidden once user has navigated to /founders-guide (no point selling
//     it to them)
//   • Hidden on /pricing /signup /login (transactional flows — don't
//     interrupt)
//   • Hidden on all /app/** dashboard routes (already authenticated)
//   • Mobile-optimised — shrinks to icon-only on narrow screens
//
// Mounted globally in App.js so it appears on every public page.

const DISMISS_KEY = "dara_sticky_cta_dismissed_until";
const DISMISS_DAYS = 7;

const HIDDEN_PATHS = new Set([
  "/founders-guide",
  "/pricing",
  "/signup",
  "/login",
  "/reset",
  "/reset-password",
]);

export default function StickyCTABar() {
  const [show, setShow] = useState(false);
  const [pathname, setPathname] = useState(
    typeof window !== "undefined" ? window.location.pathname : "/",
  );

  // Re-evaluate pathname on every history change (so SPA navigation
  // hides the bar on transactional pages without a full reload).
  useEffect(() => {
    const onLocChange = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onLocChange);
    // Patch pushState/replaceState to dispatch our own event so React
    // Router navigations trigger re-evaluation.
    const origPush = window.history.pushState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      onLocChange();
    };
    return () => {
      window.removeEventListener("popstate", onLocChange);
      window.history.pushState = origPush;
    };
  }, []);

  useEffect(() => {
    // Don't run scroll logic on hidden paths.
    if (HIDDEN_PATHS.has(pathname) || pathname.startsWith("/app/")) {
      setShow(false);
      return;
    }

    // Don't show if dismissed in the last 7 days.
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) {
        setShow(false);
        return;
      }
    } catch (e) {
      // localStorage unavailable (private mode) → just show the bar
    }

    const onScroll = () => {
      if (window.scrollY > 800) {
        setShow(true);
      } else {
        setShow(false);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  const dismiss = () => {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch (e) {
      // ignore
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#FFB000]/40 bg-black/95 backdrop-blur-md shadow-[0_-12px_36px_-12px_rgba(255,176,0,0.25)]"
      data-testid="sticky-cta-bar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="hidden sm:inline-flex items-center w-2 h-2 rounded-full bg-[#FFB000] animate-pulse flex-shrink-0" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] hidden sm:inline-block">
            // 4-PAGE FOUNDER'S GUIDE
          </div>
          <div className="font-mono text-[11px] sm:text-xs text-zinc-300 truncate">
            <span className="text-white font-bold">11 jurisdictions.</span>{" "}
            <span className="hidden sm:inline">From compliance gaps to revenue uplift.</span>{" "}
            <span className="text-zinc-500">No email required.</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to="/founders-guide"
            data-testid="sticky-cta-primary"
            className="inline-flex items-center gap-1.5 bg-[#FFB000] text-black font-bold font-mono text-[10px] sm:text-xs uppercase tracking-widest px-3 sm:px-4 py-2 hover:opacity-90"
          >
            <FileDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            <span className="hidden sm:inline">Download free</span>
            <span className="sm:hidden">Get</span>
            <ChevronRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </Link>
          <button
            type="button"
            onClick={dismiss}
            data-testid="sticky-cta-dismiss"
            className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900"
            aria-label="Dismiss for 7 days"
            title="Dismiss for 7 days"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
